import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { getAccessToken } from '../../authUtils';
import {
  COLORS, SHADOWS, Icons, Btn,
  TH, TD, ErrorBlock, EmptyState, SkeletonTable,
} from '../ui/crm-ui';

// ─── Types ───────────────────────────────────────────────────────────────────

type DispatchStatus = 'pending' | 'running' | 'complete' | 'failed' | 'cancelled';

interface Dispatch {
  id: number;
  user_id: string;
  prompt_id: number;
  prompt_name: string;
  output_file_name: string;
  status: DispatchStatus;
  result_file_name: string | null;
  result_sharepoint_url: string | null;
  result_folder_name: string | null;
  error_message: string | null;
  tokens_used: number | null;
  duration_ms: number | null;
  created_at: string;
  completed_at: string | null;
  entity_name: string | null;
  group_name: string | null;
}

interface Props {
  baseApiUrl: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function timeAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const secs = Math.floor(diffMs / 1000);
  if (secs < 5) return 'just now';
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function formatDuration(ms: number): string {
  const totalSecs = Math.floor(ms / 1000);
  const mins = Math.floor(totalSecs / 60);
  const secs = totalSecs % 60;
  if (mins > 0) return `${mins}m ${secs}s`;
  return `${secs}s`;
}

function formatTokens(n: number | null): string {
  if (n == null) return '—';
  return n.toLocaleString();
}

function isActiveStatus(status: DispatchStatus): boolean {
  return status === 'pending' || status === 'running';
}

// ─── Status Badge ─────────────────────────────────────────────────────────────

const StatusBadge: React.FC<{ status: DispatchStatus }> = ({ status }) => {
  const config: Record<DispatchStatus, { bg: string; color: string; label: string; dotClass?: string }> = {
    pending:   { bg: COLORS.light,     color: COLORS.mid,   label: 'Pending' },
    running:   { bg: COLORS.blueSoft,  color: COLORS.blue,  label: 'Running', dotClass: 'crm-anim-pulseSoft' },
    complete:  { bg: COLORS.greenSoft, color: COLORS.green, label: 'Complete' },
    failed:    { bg: '#fef2f2',        color: '#dc2626',    label: 'Failed' },
    cancelled: { bg: '#f5f5f5',        color: '#6b7280',    label: 'Cancelled' },
  };
  const { bg, color, label, dotClass } = config[status] ?? config.pending;
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '3px 10px', borderRadius: 999,
      background: bg, color, fontSize: 11, fontWeight: 600,
    }}>
      <span
        className={dotClass}
        style={{
          width: 6, height: 6, borderRadius: '50%',
          background: color, flexShrink: 0,
        }}
      />
      {label}
    </div>
  );
};

// ─── Duration Cell (live for running, static for complete/failed) ─────────────

const DurationCell: React.FC<{ dispatch: Dispatch; now: number }> = ({ dispatch, now }) => {
  if (dispatch.status === 'pending') return <span style={{ color: COLORS.mid }}>—</span>;

  if (dispatch.status === 'running') {
    const elapsedMs = now - new Date(dispatch.created_at).getTime();
    return <span style={{ color: COLORS.blue, fontVariantNumeric: 'tabular-nums' }}>{formatDuration(elapsedMs)}</span>;
  }

  if (dispatch.duration_ms != null) {
    return <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatDuration(dispatch.duration_ms)}</span>;
  }
  return <span style={{ color: COLORS.mid }}>—</span>;
};

// ─── Main component ───────────────────────────────────────────────────────────

const DispatchesView: React.FC<Props> = ({ baseApiUrl }) => {
  const [dispatches, setDispatches] = useState<Dispatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [stoppingIds, setStoppingIds] = useState<Set<number>>(new Set());

  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tickTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Live clock for elapsed-time column — ticks every second
  useEffect(() => {
    tickTimerRef.current = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      if (tickTimerRef.current) clearInterval(tickTimerRef.current);
    };
  }, []);

  const fetchDispatches = useCallback(async (isManual = false) => {
    if (isManual) setRefreshing(true);
    try {
      const token = await getAccessToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get<Dispatch[]>(`${baseApiUrl}/api/dispatches`, { headers });
      setDispatches(res.data);
      setError(null);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        (err as { message?: string })?.message ??
        'Failed to load dispatches';
      setError(msg);
    } finally {
      setLoading(false);
      if (isManual) setRefreshing(false);
    }
  }, [baseApiUrl]);

  const handleCancel = useCallback(async (dispatchId: number) => {
    setStoppingIds((prev) => new Set(prev).add(dispatchId));
    try {
      const token = await getAccessToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${baseApiUrl}/api/dispatches/${dispatchId}/cancel`, {}, { headers });
      setDispatches((prev) =>
        prev.map((d) => d.id === dispatchId ? { ...d, status: 'cancelled' as DispatchStatus } : d)
      );
    } catch (err: unknown) {
      // 409 means the dispatch completed just as we tried to cancel — refresh to show real status
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        fetchDispatches();
      } else {
        console.error('Failed to cancel dispatch:', err);
      }
    } finally {
      setStoppingIds((prev) => {
        const next = new Set(prev);
        next.delete(dispatchId);
        return next;
      });
    }
  }, [baseApiUrl, fetchDispatches]);

  // Initial fetch
  useEffect(() => {
    fetchDispatches();
  }, [fetchDispatches]);

  // Smart polling: only while there are active dispatches
  useEffect(() => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);

    const hasActive = dispatches.some((d) => isActiveStatus(d.status));
    if (!hasActive) return;

    pollTimerRef.current = setTimeout(() => {
      fetchDispatches();
    }, 10000);

    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, [dispatches, fetchDispatches]);

  // ─── Render ───────────────────────────────────────────────────────────────

  const headerIcon = (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={COLORS.blue} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
    </svg>
  );

  return (
    <div className="crm-anim-fadeUp">
      {/* Inline page header */}
      <div style={{
        position: 'relative',
        background: 'linear-gradient(120deg, rgba(18,38,170,0.06) 0%, rgba(124,58,237,0.06) 100%)',
        borderRadius: 14,
        padding: '18px 22px',
        marginBottom: 18,
        border: '1px solid rgba(18,38,170,0.08)',
        overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', left: 0, top: 0, bottom: 0, width: 4,
          background: 'linear-gradient(120deg, #1226AA 0%, #4f46e5 100%)',
        }} />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{
              width: 42, height: 42, borderRadius: 10,
              background: COLORS.white, border: `1px solid ${COLORS.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: SHADOWS.sm, flexShrink: 0,
            }}>
              {headerIcon}
            </div>
            <div>
              <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0, color: COLORS.dark, lineHeight: 1.2 }}>AI Dispatches</h2>
              <div style={{ fontSize: 13, color: COLORS.mid, marginTop: 4 }}>Track your document generation requests</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Btn
              small
              onClick={() => fetchDispatches(true)}
              disabled={refreshing}
              style={{
                background: COLORS.blueSoft,
                color: refreshing ? COLORS.mid : COLORS.blue,
                border: `1px solid ${COLORS.blue}40`,
                opacity: refreshing ? 0.6 : 1,
                cursor: refreshing ? 'not-allowed' : 'pointer',
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                {Icons.refresh(refreshing ? COLORS.mid : COLORS.blue)}
                {refreshing ? 'Refreshing...' : 'Refresh'}
              </span>
            </Btn>
          </div>
        </div>
      </div>

      {/* Body */}
      {loading ? (
        <SkeletonTable rows={5} widths={[14, 28, 20, 10, 8, 8, 12]} />
      ) : error ? (
        <ErrorBlock message={error} />
      ) : dispatches.length === 0 ? (
        <EmptyState
          icon={headerIcon}
          title="No dispatches yet. Generate a document to get started."
        />
      ) : (
        <div style={{
          background: COLORS.white,
          borderRadius: 12,
          border: `1px solid ${COLORS.border}`,
          overflow: 'hidden',
          boxShadow: SHADOWS.sm,
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <TH>Status</TH>
                <TH>Document</TH>
                <TH>Client</TH>
                <TH>Prompt</TH>
                <TH>Started</TH>
                <TH>Duration</TH>
                <TH>Tokens</TH>
                <TH style={{ width: 80 }} />
              </tr>
            </thead>
            <tbody className="crm-stagger">
              {dispatches.map((d) => {
                const displayName =
                  (d.status === 'complete' && d.result_file_name) ? d.result_file_name : d.output_file_name;

                return (
                  <tr
                    key={d.id}
                    className="crm-row-hover"
                    onMouseEnter={(e) => (e.currentTarget.style.background = COLORS.pageBg)}
                    onMouseLeave={(e) => (e.currentTarget.style.background = '')}
                  >
                    {/* Status */}
                    <TD style={{ whiteSpace: 'nowrap' }}>
                      <StatusBadge status={d.status} />
                    </TD>

                    {/* Document */}
                    <TD>
                      <div style={{ minWidth: 0 }}>
                        {d.result_sharepoint_url ? (
                          <a
                            href={d.result_sharepoint_url}
                            target="_blank"
                            rel="noreferrer"
                            style={{
                              color: COLORS.blue, fontWeight: 500,
                              textDecoration: 'none', fontSize: 13,
                            }}
                          >
                            {displayName}
                          </a>
                        ) : (
                          <span style={{ fontWeight: 500, fontSize: 13 }}>{displayName}</span>
                        )}
                        {d.status === 'failed' && d.error_message && (
                          <div style={{
                            fontSize: 11, color: '#dc2626', marginTop: 3,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            maxWidth: 280,
                          }} title={d.error_message}>
                            {d.error_message.length > 80 ? d.error_message.slice(0, 80) + '...' : d.error_message}
                          </div>
                        )}
                      </div>
                    </TD>

                    {/* Client */}
                    <TD style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                      {d.entity_name ? (
                        <div>
                          <div style={{ fontWeight: 500, color: COLORS.dark }}>{d.entity_name}</div>
                          {d.group_name && (
                            <div style={{ color: COLORS.mid, fontSize: 11 }}>{d.group_name}</div>
                          )}
                        </div>
                      ) : d.group_name ? (
                        <span style={{ color: COLORS.dark }}>{d.group_name}</span>
                      ) : (
                        <span style={{ color: COLORS.mid }}>—</span>
                      )}
                    </TD>

                    {/* Prompt */}
                    <TD style={{ color: COLORS.mid, fontSize: 12, whiteSpace: 'nowrap' }}>
                      {d.prompt_name}
                    </TD>

                    {/* Started */}
                    <TD style={{ color: COLORS.mid, fontSize: 12, whiteSpace: 'nowrap' }}>
                      {timeAgo(d.created_at)}
                    </TD>

                    {/* Duration */}
                    <TD style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                      <DurationCell dispatch={d} now={now} />
                    </TD>

                    {/* Tokens */}
                    <TD style={{ fontSize: 12, color: d.tokens_used != null ? COLORS.dark : COLORS.mid, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                      {formatTokens(d.tokens_used)}
                    </TD>

                    {/* Action */}
                    <TD>
                      {d.status === 'complete' && d.result_sharepoint_url ? (
                        <a
                          href={d.result_sharepoint_url}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            display: 'inline-block',
                            padding: '3px 10px',
                            borderRadius: 6,
                            background: COLORS.blueSoft,
                            color: COLORS.blue,
                            fontSize: 12,
                            fontWeight: 600,
                            textDecoration: 'none',
                            border: `1px solid ${COLORS.blue}30`,
                          }}
                        >
                          Open
                        </a>
                      ) : isActiveStatus(d.status) ? (
                        <Btn
                          small
                          onClick={() => handleCancel(d.id)}
                          disabled={stoppingIds.has(d.id)}
                          style={{
                            background: '#fef2f2',
                            color: stoppingIds.has(d.id) ? '#6b7280' : '#dc2626',
                            border: '1px solid #fca5a530',
                            opacity: stoppingIds.has(d.id) ? 0.6 : 1,
                            cursor: stoppingIds.has(d.id) ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {stoppingIds.has(d.id) ? 'Stopping...' : 'Stop'}
                        </Btn>
                      ) : null}
                    </TD>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DispatchesView;
